import { DOMParser as PMDOMParser } from '@tiptap/pm/model';
import { Selection } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/core';
import type { Fragment } from '@tiptap/pm/model';

/**
 * The nearest INLINE (textblock) document position at or before `pos`. After inserting
 * multi-block HTML, the write-end position can land at a doc-level block boundary (its
 * parent is the doc, not a textblock) — placing a caret widget there renders it on its
 * own line. Snap backward to the closest valid inline position so the caret rides the
 * end of the text instead.
 */
export function inlinePosAtOrBefore(editor: Editor, pos: number, doc = editor.state.doc): number {
  const clamped = Math.min(Math.max(pos, 0), doc.content.size);
  const $pos = doc.resolve(clamped);
  if ($pos.parent.isTextblock) return clamped;
  return Selection.near($pos, -1).from;
}

/**
 * Parse an HTML string into a ProseMirror Fragment using the editor's schema and the
 * native DOM parser (the same path clipboard paste uses). Returns the parsed block
 * content. If the HTML is a single paragraph, its INLINE content is returned so it can
 * be inserted into an existing block without splitting it. Returns null on failure
 * (e.g. a partial/incomplete tag mid-stream — the caller falls back to plain text).
 */
/**
 * Is the streamed HTML safe to render yet? During streaming, a chunk may end mid-tag
 * (e.g. "<p>hello <stro") — rendering that shows ugly partial/literal tags. The HTML is
 * renderable only when it does NOT end inside an unterminated tag: the last "<" must be
 * closed by a later ">". (No "<" at all is fine — plain text.)
 */
export function isRenderableHtml(html: string): boolean {
  const lastOpen = html.lastIndexOf('<');
  if (lastOpen === -1) return true;
  return html.indexOf('>', lastOpen) !== -1;
}

const VOID_TAGS = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'wbr', 'col', 'area', 'source']);

/**
 * Split accumulated streamed HTML into the fully-closed top-level blocks (`stable`) and the
 * trailing still-open block (`tail`). During streaming, completed top-level blocks never
 * change again, so the write path re-renders ONLY the tail — leaving the stable blocks'
 * nodes untouched (no DOM churn / caret flicker). The boundary is the offset just after the
 * last top-level closing tag that returns nesting depth to 0.
 */
export function splitStableHtml(html: string): { stable: string; tail: string } {
  const tagRe = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*?(\/?)>/g;
  let depth = 0;
  let stableEnd = 0;
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(html)) !== null) {
    const [full, name, selfClose] = m;
    const isClose = full.startsWith('</');
    if (isClose) {
      depth -= 1;
      if (depth === 0) stableEnd = m.index + full.length;
    } else if (selfClose || VOID_TAGS.has(name.toLowerCase())) {
      if (depth === 0) stableEnd = m.index + full.length;
    } else {
      depth += 1;
    }
  }
  return { stable: html.slice(0, stableEnd), tail: html.slice(stableEnd) };
}

export function parseHtmlToFragment(editor: Editor, html: string): Fragment | null {
  try {
    const dom = document.createElement('div');
    dom.innerHTML = html;
    const parsed = PMDOMParser.fromSchema(editor.state.schema).parse(dom);
    // A single top-level paragraph → use its inline content so it stays in the current
    // block. Otherwise (headings, lists, multiple blocks) use the block content.
    if (parsed.childCount === 1 && parsed.firstChild?.type.name === 'paragraph') {
      return parsed.firstChild.content;
    }
    return parsed.content;
  } catch {
    return null;
  }
}
